/**
 * The ticket lifecycle (PLAN.md §2) as one pure function: state × actor × action →
 * next state, or the reason it is refused. The API applies the result; it never
 * decides a transition itself. Exhaustively tested in tickets.test.ts.
 */

export const ticketStatuses = ['open', 'discussing', 'proposed', 'approved', 'in_progress', 'done', 'closed', 'declined', 'cancelled', 'paused'] as const;
export type TicketStatus = (typeof ticketStatuses)[number];

/** Statuses nothing can leave (reopen aside, see `declined`/`done`). */
export const terminalStatuses: readonly TicketStatus[] = ['closed', 'cancelled'];

export const ticketActions = [
  'message',
  'propose',
  'decline',
  'reopen',
  'cancel',
  'close',
  'approve',
  'reject',
  'start',
  'progress',
  'done',
  'pause',
  'resume',
  'notes',
  'request_human'
] as const;
export type TicketAction = (typeof ticketActions)[number];

/** How the caller relates to the ticket and through what it acts. */
export type TicketActor = {
  requester: boolean;
  assignee: boolean;
  admin: boolean;
  kind: 'assistant' | 'worker' | 'human';
};

export type TicketState = {
  status: TicketStatus;
  /** Set while paused: the status to resume to. */
  pausedFrom: TicketStatus | null;
  reopenedAfterDecline: boolean;
  reopenedAfterDone: boolean;
};

export type TransitionError = 'not_participant' | 'wrong_actor' | 'wrong_status' | 'paused' | 'reopen_used';

export type TransitionResult =
  | { ok: true; state: TicketState; /** True when the status changed (resets the message streak). */ changed: boolean }
  | { ok: false; error: TransitionError };

type Rule = {
  /** Who may: the role on the ticket and the channel they act through. */
  who: (a: TicketActor) => boolean;
  from: readonly TicketStatus[];
  to: (s: TicketState) => TicketStatus;
};

const ACTIVE: readonly TicketStatus[] = ['open', 'discussing', 'proposed', 'approved', 'in_progress', 'done'];
const assistantOf = (role: 'requester' | 'assignee' | 'either') => (a: TicketActor) =>
  a.kind === 'assistant' && (role === 'either' ? a.requester || a.assignee : a[role]);
const assigneeHuman = (a: TicketActor) => a.kind === 'human' && a.assignee;
const assigneeWorker = (a: TicketActor) => a.kind === 'worker' && a.assignee;
/** Pause and resume: either human on the ticket, or an admin (the kill switch, PLAN.md §8). */
const participantHuman = (a: TicketActor) => a.kind === 'human' && (a.requester || a.assignee || a.admin);

const rules: Record<TicketAction, Rule> = {
  // A message moves a fresh ticket into discussion once the assignee's Assistant answers.
  message: { who: assistantOf('either'), from: ACTIVE, to: (s) => s.status },
  notes: { who: assistantOf('either'), from: [...ACTIVE, 'declined'], to: (s) => s.status },
  request_human: { who: assistantOf('either'), from: [...ACTIVE, 'declined'], to: (s) => s.status },
  propose: { who: assistantOf('assignee'), from: ['open', 'discussing', 'proposed', 'approved'], to: () => 'proposed' },
  decline: { who: assistantOf('assignee'), from: ['open', 'discussing', 'proposed'], to: () => 'declined' },
  reopen: { who: assistantOf('requester'), from: ['declined', 'done'], to: () => 'discussing' },
  cancel: { who: assistantOf('requester'), from: ['open', 'discussing', 'proposed', 'approved'], to: () => 'cancelled' },
  close: { who: assistantOf('requester'), from: ['done'], to: () => 'closed' },
  approve: { who: assigneeHuman, from: ['proposed'], to: () => 'approved' },
  reject: { who: assigneeHuman, from: ['proposed'], to: () => 'discussing' },
  start: { who: assigneeWorker, from: ['approved'], to: () => 'in_progress' },
  progress: { who: assigneeWorker, from: ['in_progress'], to: (s) => s.status },
  done: { who: assigneeWorker, from: ['in_progress'], to: () => 'done' },
  pause: { who: participantHuman, from: [...ACTIVE, 'declined'], to: () => 'paused' },
  resume: { who: participantHuman, from: ['paused'], to: (s) => s.pausedFrom ?? 'discussing' }
};

export function transition(state: TicketState, actor: TicketActor, action: TicketAction): TransitionResult {
  const rule = rules[action];
  if (!actor.requester && !actor.assignee && !(actor.admin && actor.kind === 'human' && (action === 'pause' || action === 'resume'))) {
    return { ok: false, error: 'not_participant' };
  }
  if (!rule.who(actor)) return { ok: false, error: 'wrong_actor' };
  if (state.status === 'paused' && action !== 'resume') return { ok: false, error: 'paused' };
  if (!rule.from.includes(state.status)) return { ok: false, error: 'wrong_status' };

  if (action === 'reopen') {
    if (state.status === 'declined' && state.reopenedAfterDecline) return { ok: false, error: 'reopen_used' };
    if (state.status === 'done' && state.reopenedAfterDone) return { ok: false, error: 'reopen_used' };
  }

  let to = rule.to(state);
  // The assignee's Assistant picking up a fresh ticket starts the discussion.
  if (action === 'message' && state.status === 'open' && actor.assignee) to = 'discussing';

  const next: TicketState = {
    status: to,
    pausedFrom: action === 'pause' ? state.status : action === 'resume' ? null : state.pausedFrom,
    reopenedAfterDecline: state.reopenedAfterDecline || (action === 'reopen' && state.status === 'declined'),
    reopenedAfterDone: state.reopenedAfterDone || (action === 'reopen' && state.status === 'done')
  };
  return { ok: true, state: next, changed: to !== state.status };
}

/** Who gets an inbox item for an action: the ticket's participants except an Assistant acting itself. */
export function inboxRecipients(ticket: { requesterId: string; assigneeId: string }, actor: { userId: string | null; kind: 'assistant' | 'worker' | 'human' | 'system' }): string[] {
  const participants = [...new Set([ticket.requesterId, ticket.assigneeId])];
  return participants.filter((u) => !(actor.kind === 'assistant' && u === actor.userId));
}

/** `TAI-12` or `12` → 12; null for anything else. */
export function parseTicketNumber(ref: string): number | null {
  const m = /^(?:TAI-)?(\d{1,9})$/i.exec(ref.trim());
  return m ? Number(m[1]) : null;
}

export const ticketLabel = (n: number) => `TAI-${n}`;
